import { useEffect, useMemo, useRef } from 'react';
import { Button, Checkbox, DatePicker, Form, Input, Steps, Typography } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

import logoDocsRedDot from '@assets/logo-docs-red-dot.png';
import {
    areOriginDestinationAddressesSame,
    ORIGIN_DESTINATION_SAME_ADDRESS_MESSAGE
} from '@utils/addressMatch';

import {
    AgentAssistAvatar,
    AgentAssistBar,
    AgentAssistIdentity,
    AgentAssistName,
    EdocFieldsGrid,
    EdocGrid,
    EdocSectionCard,
    EdocSectionTitle,
    EdocStepsWrap,
    EdocSummaryStack,
    EdocTopRow,
    EdocVerifiedCard,
    EdocVerifiedShell,
    EdocVerifiedViewport
} from '@/pages/style';

const { TextArea } = Input;

const safeText = (v) => {
    const s = String(v ?? '').trim();
    return s ? s : '—';
};

const formatMoney = (value) => {
    if (value == null || String(value).trim() === '') return null;
    const n = Number(value);
    if (!Number.isFinite(n)) {
        const raw = String(value).trim();
        return raw || null;
    }
    return `$${n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;
};

const resolvePaymentDisplay = (snapshot = {}, payment = {}) => {
    const totalTariff =
        payment?.amountDueByClient
        ?? payment?.totalTariff
        ?? snapshot?.amountDueByClient
        ?? snapshot?.totalTariff
        ?? snapshot?.amount
        ?? null;
    const paidAmount = payment?.paidAmount ?? snapshot?.paidAmount ?? 0;
    const remainingDue =
        payment?.remainingDue
        ?? snapshot?.remainingDue
        ?? null;
    const totalFormatted =
        payment?.totalTariffFormatted
        || formatMoney(totalTariff)
        || (typeof snapshot?.paymentDue === 'string' && snapshot.paymentDue.trim()
            ? snapshot.paymentDue.trim()
            : null);
    const remainingFormatted =
        payment?.remainingDueFormatted
        || formatMoney(remainingDue);

    return {
        totalFormatted: totalFormatted || '—',
        remainingFormatted:
            Number(paidAmount) > 0 && remainingFormatted
                ? remainingFormatted
                : null,
        paidFormatted: Number(paidAmount) > 0
            ? (payment?.paidAmountFormatted || formatMoney(paidAmount))
            : null
    };
};

const formatPrettyDate = (value) => {
    if (!value) return '—';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '—';

    const day = d.getDate();
    const mod10 = day % 10;
    const mod100 = day % 100;
    const suffix =
        mod10 === 1 && mod100 !== 11
            ? 'st'
            : mod10 === 2 && mod100 !== 12
                ? 'nd'
                : mod10 === 3 && mod100 !== 13
                    ? 'rd'
                    : 'th';

    const month = d.toLocaleString(undefined, { month: 'long' });
    const year = d.getFullYear();
    return `${day}${suffix} ${month} ${year}`;
};

/** Form.List for emails/phones only mounts on Order info; use fallback when copying "I am the contact". */
const resolveOrderContactEmailPhone = (edocForm, fallback = {}) => {
    const emails = edocForm.getFieldValue(['contact', 'emails']);
    const phones = edocForm.getFieldValue(['contact', 'phones']);
    const fromEmail = Array.isArray(emails) ? String(emails[0] ?? '').trim() : '';
    const fromPhone = Array.isArray(phones) ? String(phones[0] ?? '').trim() : '';
    return {
        email: fromEmail || String(fallback.email ?? '').trim(),
        phone: fromPhone || String(fallback.phone ?? '').trim()
    };
};

const StepActionLink = ({ children, ...props }) => (
    <Typography.Link
        {...props}
        style={{
            color: '#a5d8ff',
            width: 'fit-content',
            ...(props.style || {})
        }}
        onMouseEnter={(e) => {
            e.currentTarget.style.color = '#4dabf7';
            props.onMouseEnter?.(e);
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.color = '#a5d8ff';
            props.onMouseLeave?.(e);
        }}
    >
        {children}
    </Typography.Link>
);

export const EdocVerifiedView = ({
    isDark,
    assignedUserLabel,

    activeStep,
    setActiveStep,
    steps,

    edocForm,

    showCompany,
    setShowCompany,
    showAltOriginLine,
    setShowAltOriginLine,
    showAltDestinationLine,
    setShowAltDestinationLine,

    agreeTerms,
    setAgreeTerms,
    signatureName,
    setSignatureName,
    ipAddress,
    agreedAtText,

    snapshot,
    payment,
    packages,
    companyInfo,
    orderContactFallback,
    onBookOrder,
    booking
}) => {
    const termsMarkdown = useMemo(
        () => String(companyInfo?.termsAndConditions || companyInfo?.termAndConditions || '').trim(),
        [companyInfo?.termsAndConditions, companyInfo?.termAndConditions]
    );

    const paymentDisplay = useMemo(
        () => resolvePaymentDisplay(snapshot, payment),
        [snapshot, payment]
    );

    const packageLines = useMemo(() => {
        const vehicles = Array.isArray(packages?.vehicles)
            ? packages.vehicles
            : (Array.isArray(snapshot?.packages?.vehicles) ? snapshot.packages.vehicles : []);
        const others = Array.isArray(packages?.others)
            ? packages.others
            : (Array.isArray(snapshot?.packages?.others) ? snapshot.packages.others : []);
        const lines = [];
        for (const v of vehicles) {
            const label = [v?.year, v?.make, v?.model, v?.type].filter(Boolean).join(' ').trim();
            if (label) lines.push(label);
        }
        for (const o of others) {
            const label = [o?.type, o?.name, o?.description].filter(Boolean).join(' — ').trim();
            if (label) lines.push(label);
        }
        return lines;
    }, [packages, snapshot]);

    const canBookOrder = Boolean(agreeTerms && String(signatureName || '').trim().length > 0);
    const requiredActivationRef = useRef({});

    const makeActivatedRequiredRule = (key, message) => ({
        validator: async (_, value) => {
            const v = String(value ?? '').trim();
            if (v) return;
            if (!requiredActivationRef.current[key]) return;
            throw new Error(message);
        }
    });

    const activateRequiredKeys = (keys) => {
        keys.forEach((k) => {
            requiredActivationRef.current[k] = true;
        });
    };

    const activateKeyIfPrefilled = (key, currentValue) => {
        if (requiredActivationRef.current[key]) return;
        if (String(currentValue ?? '').trim()) requiredActivationRef.current[key] = true;
    };

    const validateStepAndNext = async () => {
        // Validate only the fields relevant to the current step.
        const byStep = {
            0: [['contact', 'firstName']],
            1: [['origin', 'address1'], ['origin', 'city'], ['origin', 'state']],
            2: [
                ['destination', 'address1'],
                ['destination', 'city'],
                ['destination', 'state']
            ]
        };
        const activateByStep = {
            1: ['origin.address1', 'origin.city', 'origin.state'],
            2: ['destination.address1', 'destination.city', 'destination.state']
        };
        if (activateByStep[activeStep]) activateRequiredKeys(activateByStep[activeStep]);
        const fields = byStep[activeStep] || [];
        if (!fields.length) {
            setActiveStep((s) => Math.min(steps.length - 1, s + 1));
            return;
        }
        try {
            await edocForm.validateFields(fields);
            if (activeStep === 1 || activeStep === 2) {
                const values = edocForm.getFieldsValue(true);
                if (areOriginDestinationAddressesSame(values?.origin, values?.destination)) {
                    const target = activeStep === 1
                        ? [['origin', 'address1']]
                        : [['destination', 'address1']];
                    edocForm.setFields(
                        target.map((name) => ({
                            name,
                            errors: [ORIGIN_DESTINATION_SAME_ADDRESS_MESSAGE]
                        }))
                    );
                    return;
                }
            }
            setActiveStep((s) => Math.min(steps.length - 1, s + 1));
        } catch {
            // AntD will display field-level errors; do nothing else here.
        }
    };

    const sameAddressRule = {
        validator: async () => {
            const values = edocForm.getFieldsValue(true);
            if (areOriginDestinationAddressesSame(values?.origin, values?.destination)) {
                throw new Error(ORIGIN_DESTINATION_SAME_ADDRESS_MESSAGE);
            }
        }
    };

    return (
        <EdocVerifiedViewport>
            <EdocVerifiedShell>
                <EdocTopRow>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <img
                            src={logoDocsRedDot}
                            alt="Dispatchly logo"
                            style={{ width: 160, height: 'auto', marginBottom: 6 }}
                        />
                        <Typography.Title level={3} style={{ margin: 0 }}>
                            Book My Shipment
                        </Typography.Title>
                        <Typography.Text type="secondary">
                            Fill out the form below to book your transportation order.
                        </Typography.Text>
                    </div>

                    <AgentAssistBar style={{ marginBottom: 12 }}>
                        <AgentAssistIdentity>
                            <AgentAssistAvatar>
                                <UserOutlined />
                            </AgentAssistAvatar>
                            <AgentAssistName title={`Assigned Agent: ${assignedUserLabel}`}>
                                Assigned Agent: {assignedUserLabel}
                            </AgentAssistName>
                        </AgentAssistIdentity>
                    </AgentAssistBar>
                </EdocTopRow>

                <EdocVerifiedCard>
                    <EdocStepsWrap>
                        <Steps
                            current={activeStep}
                            items={steps}
                            responsive
                        />
                    </EdocStepsWrap>

                    <EdocGrid>
                        <EdocSectionCard>
                            <EdocSectionTitle>{steps[activeStep]?.title}</EdocSectionTitle>

                            {activeStep === 0 ? (
                                <OrderInfoStep
                                    edocForm={edocForm}
                                    snapshot={snapshot}
                                    showCompany={showCompany}
                                    setShowCompany={setShowCompany}
                                    packageLines={packageLines}
                                />
                            ) : null}

                            {activeStep === 1 ? (
                                <OriginStep
                                    edocForm={edocForm}
                                    orderContactFallback={orderContactFallback}
                                    showAltOriginLine={showAltOriginLine}
                                    setShowAltOriginLine={setShowAltOriginLine}
                                    makeActivatedRequiredRule={makeActivatedRequiredRule}
                                    activateKeyIfPrefilled={activateKeyIfPrefilled}
                                    sameAddressRule={sameAddressRule}
                                />
                            ) : null}

                            {activeStep === 2 ? (
                                <DestinationStep
                                    edocForm={edocForm}
                                    orderContactFallback={orderContactFallback}
                                    showAltDestinationLine={showAltDestinationLine}
                                    setShowAltDestinationLine={setShowAltDestinationLine}
                                    makeActivatedRequiredRule={makeActivatedRequiredRule}
                                    activateKeyIfPrefilled={activateKeyIfPrefilled}
                                    sameAddressRule={sameAddressRule}
                                />
                            ) : null}

                            {activeStep === 3 ? (
                                <TermsStep
                                    isDark={isDark}
                                    agreeTerms={agreeTerms}
                                    setAgreeTerms={setAgreeTerms}
                                    signatureName={signatureName}
                                    setSignatureName={setSignatureName}
                                    ipAddress={ipAddress}
                                    agreedAtText={agreedAtText}
                                    termsMarkdown={termsMarkdown}
                                />
                            ) : null}

                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginTop: 16,
                                    gap: 10,
                                    flexWrap: 'wrap'
                                }}
                            >
                                <Button
                                    type="default"
                                    disabled={activeStep === 0}
                                    onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
                                >
                                    Previous
                                </Button>

                                {activeStep === steps.length - 1 ? (
                                    <Button
                                        type="primary"
                                        disabled={!canBookOrder || booking}
                                        loading={booking}
                                        onClick={onBookOrder}
                                    >
                                        Book My Order
                                    </Button>
                                ) : (
                                    <Button
                                        type="primary"
                                        onClick={validateStepAndNext}
                                    >
                                        Next
                                    </Button>
                                )}
                            </div>
                        </EdocSectionCard>

                        <EdocSummaryStack>
                            <EdocSectionCard>
                                <EdocSectionTitle>Quote</EdocSectionTitle>
                                <div style={{ display: 'grid', gap: 8 }}>
                                    <SummaryRow label="Quote #" value={safeText(snapshot?.trackingNumber)} />
                                    <SummaryRow label="Created At" value={formatPrettyDate(snapshot?.quoteCreated)} />
                                </div>
                            </EdocSectionCard>

                            <EdocSectionCard>
                                <EdocSectionTitle>Payment</EdocSectionTitle>
                                <div style={{ display: 'grid', gap: 10 }}>
                                    <SummaryRow
                                        label="Amount Due by Client"
                                        value={paymentDisplay.totalFormatted}
                                    />
                                    {paymentDisplay.paidFormatted ? (
                                        <SummaryRow
                                            label="Paid so far"
                                            value={paymentDisplay.paidFormatted}
                                        />
                                    ) : null}
                                    {paymentDisplay.remainingFormatted ? (
                                        <SummaryRow
                                            label="Remaining due"
                                            value={paymentDisplay.remainingFormatted}
                                        />
                                    ) : null}
                                </div>
                            </EdocSectionCard>
                        </EdocSummaryStack>
                    </EdocGrid>
                </EdocVerifiedCard>
            </EdocVerifiedShell>
        </EdocVerifiedViewport>
    );
};

const SummaryRow = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
        <Typography.Text type="secondary">{label}</Typography.Text>
        <Typography.Text strong>{value}</Typography.Text>
    </div>
);

const OrderInfoStep = ({ edocForm, snapshot, showCompany, setShowCompany, packageLines = [] }) => (
    <Form form={edocForm} layout="vertical" requiredMark={false} style={{ marginTop: 6 }}>
        <EdocFieldsGrid>
            <Form.Item label="Transport type">
                <Input value={String(snapshot?.transportType || '').trim()} disabled placeholder="—" />
            </Form.Item>
        </EdocFieldsGrid>

        <div style={{ marginTop: 4, marginBottom: 12 }}>
            <Typography.Text style={{ fontWeight: 700 }}>Package Details</Typography.Text>
            <Typography.Paragraph type="secondary" style={{ margin: '4px 0 8px' }}>
                Optional — you can add or edit package notes before signing.
            </Typography.Paragraph>
            {packageLines.length > 0 ? (
                <div
                    style={{
                        marginBottom: 10,
                        padding: '8px 10px',
                        borderRadius: 8,
                        background: 'rgba(0,0,0,0.04)',
                        display: 'grid',
                        gap: 4
                    }}
                >
                    {packageLines.map((line, idx) => (
                        <Typography.Text key={`pkg-${idx}`}>{line}</Typography.Text>
                    ))}
                </div>
            ) : null}
            <Form.Item name="packageDetails" style={{ marginBottom: 0 }}>
                <TextArea
                    rows={3}
                    placeholder="Describe vehicles, freight, or special handling (optional)"
                    maxLength={4000}
                    showCount
                />
            </Form.Item>
        </div>

        <EdocFieldsGrid>
            <Form.Item
                name={['contact', 'firstName']}
                label="First name"
                rules={[{ required: true, message: 'First name is required.' }]}
            >
                <Input placeholder="First name" disabled />
            </Form.Item>
            <Form.Item name={['contact', 'lastName']} label="Last name">
                <Input placeholder="Last name" disabled />
            </Form.Item>
        </EdocFieldsGrid>

        {showCompany ? (
            <Form.Item name={['contact', 'company']} label="Company (optional)">
                <Input placeholder="Company name" />
            </Form.Item>
        ) : (
            <StepActionLink onClick={() => setShowCompany(true)}>+ Add Company</StepActionLink>
        )}

        <div style={{ marginTop: 12 }}>
            <Typography.Text style={{ fontWeight: 700 }}>Email</Typography.Text>
            <Form.List name={['contact', 'emails']}>
                {(fields, { add, remove }) => (
                    <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
                        {fields.map((field, idx) => (
                            <div key={field.key} style={{ display: 'flex', gap: 10 }}>
                                {(() => {
                                    // Avoid spreading React "key" into Form.Item props (React warning).
                                    // AntD Form.List field objects include `key`.
                                    // eslint-disable-next-line no-unused-vars
                                    const { key, ...restField } = field;
                                    return (
                                        <Form.Item
                                            key={key}
                                            {...restField}
                                            style={{ flex: 1, marginBottom: 0 }}
                                            rules={[
                                                { required: idx === 0, message: 'Email is required.' },
                                                { type: 'email', message: 'Enter a valid email.' }
                                            ]}
                                        >
                                            <Input placeholder="Email address" disabled={idx === 0} />
                                        </Form.Item>
                                    );
                                })()}
                                {fields.length > 1 && idx > 0 ? (
                                    <Button type="text" onClick={() => remove(field.name)}>
                                        Remove
                                    </Button>
                                ) : null}
                            </div>
                        ))}
                        <StepActionLink onClick={() => add('')}>
                            + Add Another Email
                        </StepActionLink>
                    </div>
                )}
            </Form.List>
        </div>

        <div style={{ marginTop: 12 }}>
            <Typography.Text style={{ fontWeight: 700 }}>Phone</Typography.Text>
            <Form.List name={['contact', 'phones']}>
                {(fields, { add, remove }) => (
                    <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
                        {fields.map((field, idx) => (
                            <div key={field.key} style={{ display: 'flex', gap: 10 }}>
                                {(() => {
                                    // Avoid spreading React "key" into Form.Item props (React warning).
                                    // eslint-disable-next-line no-unused-vars
                                    const { key, ...restField } = field;
                                    return (
                                        <Form.Item
                                            key={key}
                                            {...restField}
                                            style={{ flex: 1, marginBottom: 0 }}
                                            rules={[{ required: idx === 0, message: 'Phone is required.' }]}
                                        >
                                            <Input placeholder="Phone number" />
                                        </Form.Item>
                                    );
                                })()}
                                {fields.length > 1 && idx > 0 ? (
                                    <Button type="text" onClick={() => remove(field.name)}>
                                        Remove
                                    </Button>
                                ) : null}
                            </div>
                        ))}
                        <StepActionLink onClick={() => add('')}>+ Add Another Phone</StepActionLink>
                    </div>
                )}
            </Form.List>
        </div>

        <div style={{ marginTop: 12 }}>
            <Form.Item name={['contact', 'pickupDate']} label="First Available Pickup Date">
                <DatePicker style={{ width: '100%' }} disabled allowClear={false} />
            </Form.Item>
        </div>
    </Form>
);

const emptyLegContactFields = {
    contactFirstName: '',
    contactLastName: '',
    contactEmail: '',
    contactPhone: ''
};

const OriginStep = ({
    edocForm,
    orderContactFallback,
    showAltOriginLine,
    setShowAltOriginLine,
    makeActivatedRequiredRule,
    activateKeyIfPrefilled,
    sameAddressRule
}) => {
    useEffect(() => {
        // Only show required errors during editing if field was prefilled.
        activateKeyIfPrefilled?.('origin.address1', edocForm.getFieldValue(['origin', 'address1']));
        activateKeyIfPrefilled?.('origin.city', edocForm.getFieldValue(['origin', 'city']));
        activateKeyIfPrefilled?.('origin.state', edocForm.getFieldValue(['origin', 'state']));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        const locked = Boolean(edocForm.getFieldValue(['origin', 'isPickupContact']));
        if (!locked) return;
        const curEmail = String(edocForm.getFieldValue(['origin', 'contactEmail']) || '').trim();
        const curPhone = String(edocForm.getFieldValue(['origin', 'contactPhone']) || '').trim();
        if (curEmail && curPhone) return;
        const { email: fbEmail, phone: fbPhone } = resolveOrderContactEmailPhone(
            edocForm,
            orderContactFallback || {}
        );
        if (!fbEmail && !fbPhone) return;
        const prev = edocForm.getFieldValue('origin') || {};
        edocForm.setFieldsValue({
            origin: {
                ...prev,
                contactEmail: curEmail || fbEmail,
                contactPhone: curPhone || fbPhone
            }
        });
    }, [edocForm, orderContactFallback]);

    return (
        <Form form={edocForm} layout="vertical" requiredMark={false} style={{ marginTop: 6 }}>
            <Form.Item
                name={['origin', 'address1']}
                label="Origin Address"
                validateTrigger={['onBlur', 'onChange']}
                dependencies={[
                    ['origin', 'address2'],
                    ['origin', 'city'],
                    ['origin', 'state'],
                    ['origin', 'postalCode'],
                    ['destination', 'address1'],
                    ['destination', 'address2'],
                    ['destination', 'city'],
                    ['destination', 'state'],
                    ['destination', 'postalCode']
                ]}
                rules={[
                    makeActivatedRequiredRule('origin.address1', 'Origin address is required.'),
                    sameAddressRule
                ]}
            >
                <Input placeholder="Origin address" />
            </Form.Item>

            {showAltOriginLine ? (
                <Form.Item
                    name={['origin', 'address2']}
                    label="Address line 2 (optional)"
                    validateTrigger={['onBlur', 'onChange']}
                    dependencies={[
                        ['origin', 'address1'],
                        ['origin', 'city'],
                        ['origin', 'state'],
                        ['origin', 'postalCode'],
                        ['destination', 'address1'],
                        ['destination', 'address2'],
                        ['destination', 'city'],
                        ['destination', 'state'],
                        ['destination', 'postalCode']
                    ]}
                    rules={[sameAddressRule]}
                >
                    <Input placeholder="Add another address line" />
                </Form.Item>
            ) : (
                <StepActionLink onClick={() => setShowAltOriginLine(true)}>+ Add Another Address Line</StepActionLink>
            )}

            <EdocFieldsGrid style={{ marginTop: 10 }}>
                <Form.Item
                    name={['origin', 'city']}
                    label="Origin City"
                    validateTrigger="onBlur"
                    rules={[makeActivatedRequiredRule('origin.city', 'City is required.')]}
                >
                    <Input placeholder="City" />
                </Form.Item>
                <Form.Item
                    name={['origin', 'state']}
                    label="State"
                    validateTrigger="onBlur"
                    rules={[makeActivatedRequiredRule('origin.state', 'State is required.')]}
                >
                    <Input placeholder="State" />
                </Form.Item>
                <Form.Item name={['origin', 'postalCode']} label="Postal code">
                    <Input placeholder="Postal code" />
                </Form.Item>
            </EdocFieldsGrid>

            <div style={{ marginTop: 10 }}>
                <Typography.Text style={{ fontWeight: 700 }}>Origin Contact</Typography.Text>
                <div style={{ marginTop: 8 }}>
                    <Form.Item name={['origin', 'isPickupContact']} valuePropName="checked" style={{ marginBottom: 10 }}>
                        <Checkbox
                            onChange={(e) => {
                                const checked = e.target.checked;
                                const prev = edocForm.getFieldValue('origin') || {};
                                if (checked) {
                                    const first = String(edocForm.getFieldValue(['contact', 'firstName']) || '').trim();
                                    const last = String(edocForm.getFieldValue(['contact', 'lastName']) || '').trim();
                                    const { email, phone } = resolveOrderContactEmailPhone(
                                        edocForm,
                                        orderContactFallback || {}
                                    );
                                    edocForm.setFieldsValue({
                                        origin: {
                                            ...prev,
                                            contactFirstName: first,
                                            contactLastName: last,
                                            contactEmail: email,
                                            contactPhone: phone
                                        }
                                    });
                                } else {
                                    edocForm.setFieldsValue({
                                        origin: { ...prev, ...emptyLegContactFields }
                                    });
                                }
                            }}
                        >
                            I Am The Pickup Contact
                        </Checkbox>
                    </Form.Item>
                </div>

                <Form.Item shouldUpdate noStyle>
                    {({ getFieldValue }) => {
                        const locked = Boolean(getFieldValue(['origin', 'isPickupContact']));
                        return (
                            <div style={{ display: 'grid', gap: 12 }}>
                                <EdocFieldsGrid>
                                    <Form.Item name={['origin', 'contactFirstName']} label="Origin Contact Name">
                                        <Input placeholder="First name" disabled={locked} />
                                    </Form.Item>
                                    <Form.Item name={['origin', 'contactLastName']} label=" " colon={false}>
                                        <Input placeholder="Last name" disabled={locked} />
                                    </Form.Item>
                                </EdocFieldsGrid>
                                <Form.Item name={['origin', 'contactEmail']} label="Origin Contact Email">
                                    <Input placeholder="Email" disabled={locked} />
                                </Form.Item>
                                <Form.Item name={['origin', 'contactPhone']} label="Origin Contact Phone">
                                    <Input placeholder="Phone" disabled={locked} />
                                </Form.Item>
                            </div>
                        );
                    }}
                </Form.Item>
            </div>
        </Form>
    );
};

const DestinationStep = ({
    edocForm,
    orderContactFallback,
    showAltDestinationLine,
    setShowAltDestinationLine,
    makeActivatedRequiredRule,
    activateKeyIfPrefilled,
    sameAddressRule
}) => {
    useEffect(() => {
        // Only show required errors during editing if field was prefilled.
        activateKeyIfPrefilled?.('destination.address1', edocForm.getFieldValue(['destination', 'address1']));
        activateKeyIfPrefilled?.('destination.city', edocForm.getFieldValue(['destination', 'city']));
        activateKeyIfPrefilled?.('destination.state', edocForm.getFieldValue(['destination', 'state']));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        const locked = Boolean(edocForm.getFieldValue(['destination', 'isDestinationContact']));
        if (!locked) return;
        const curEmail = String(edocForm.getFieldValue(['destination', 'contactEmail']) || '').trim();
        const curPhone = String(edocForm.getFieldValue(['destination', 'contactPhone']) || '').trim();
        if (curEmail && curPhone) return;
        const { email: fbEmail, phone: fbPhone } = resolveOrderContactEmailPhone(
            edocForm,
            orderContactFallback || {}
        );
        if (!fbEmail && !fbPhone) return;
        const prev = edocForm.getFieldValue('destination') || {};
        edocForm.setFieldsValue({
            destination: {
                ...prev,
                contactEmail: curEmail || fbEmail,
                contactPhone: curPhone || fbPhone
            }
        });
    }, [edocForm, orderContactFallback]);

    return (
        <Form form={edocForm} layout="vertical" requiredMark={false} style={{ marginTop: 6 }}>
            <Form.Item
                name={['destination', 'address1']}
                label="Destination Address"
                validateTrigger={['onBlur', 'onChange']}
                dependencies={[
                    ['destination', 'address2'],
                    ['destination', 'city'],
                    ['destination', 'state'],
                    ['destination', 'postalCode'],
                    ['origin', 'address1'],
                    ['origin', 'address2'],
                    ['origin', 'city'],
                    ['origin', 'state'],
                    ['origin', 'postalCode']
                ]}
                rules={[
                    makeActivatedRequiredRule('destination.address1', 'Destination address is required.'),
                    sameAddressRule
                ]}
            >
                <Input placeholder="Destination address" />
            </Form.Item>

            {showAltDestinationLine ? (
                <Form.Item
                    name={['destination', 'address2']}
                    label="Address line 2 (optional)"
                    validateTrigger={['onBlur', 'onChange']}
                    dependencies={[
                        ['destination', 'address1'],
                        ['destination', 'city'],
                        ['destination', 'state'],
                        ['destination', 'postalCode'],
                        ['origin', 'address1'],
                        ['origin', 'address2'],
                        ['origin', 'city'],
                        ['origin', 'state'],
                        ['origin', 'postalCode']
                    ]}
                    rules={[sameAddressRule]}
                >
                    <Input placeholder="Add another address line" />
                </Form.Item>
            ) : (
                <StepActionLink onClick={() => setShowAltDestinationLine(true)}>+ Add Another Address Line</StepActionLink>
            )}

            <EdocFieldsGrid style={{ marginTop: 10 }}>
                <Form.Item
                    name={['destination', 'city']}
                    label="Destination City"
                    validateTrigger="onBlur"
                    rules={[makeActivatedRequiredRule('destination.city', 'City is required.')]}
                >
                    <Input placeholder="City" />
                </Form.Item>
                <Form.Item
                    name={['destination', 'state']}
                    label="State"
                    validateTrigger="onBlur"
                    rules={[makeActivatedRequiredRule('destination.state', 'State is required.')]}
                >
                    <Input placeholder="State" />
                </Form.Item>
                <Form.Item name={['destination', 'postalCode']} label="Postal code">
                    <Input placeholder="Postal code" />
                </Form.Item>
            </EdocFieldsGrid>

            <div style={{ marginTop: 10 }}>
                <Typography.Text style={{ fontWeight: 700 }}>Destination Contact</Typography.Text>
                <div style={{ marginTop: 8 }}>
                    <Form.Item
                        name={['destination', 'isDestinationContact']}
                        valuePropName="checked"
                        style={{ marginBottom: 10 }}
                    >
                        <Checkbox
                            onChange={(e) => {
                                const checked = e.target.checked;
                                const prev = edocForm.getFieldValue('destination') || {};
                                if (checked) {
                                    const first = String(edocForm.getFieldValue(['contact', 'firstName']) || '').trim();
                                    const last = String(edocForm.getFieldValue(['contact', 'lastName']) || '').trim();
                                    const { email, phone } = resolveOrderContactEmailPhone(
                                        edocForm,
                                        orderContactFallback || {}
                                    );
                                    edocForm.setFieldsValue({
                                        destination: {
                                            ...prev,
                                            contactFirstName: first,
                                            contactLastName: last,
                                            contactEmail: email,
                                            contactPhone: phone
                                        }
                                    });
                                } else {
                                    edocForm.setFieldsValue({
                                        destination: { ...prev, ...emptyLegContactFields }
                                    });
                                }
                            }}
                        >
                            I Am The Destination Contact
                        </Checkbox>
                    </Form.Item>
                </div>

                <Form.Item shouldUpdate noStyle>
                    {({ getFieldValue }) => {
                        const locked = Boolean(getFieldValue(['destination', 'isDestinationContact']));
                        return (
                            <div style={{ display: 'grid', gap: 12 }}>
                                <EdocFieldsGrid>
                                    <Form.Item name={['destination', 'contactFirstName']} label="Destination Contact Name">
                                        <Input placeholder="First name" disabled={locked} />
                                    </Form.Item>
                                    <Form.Item name={['destination', 'contactLastName']} label=" " colon={false}>
                                        <Input placeholder="Last name" disabled={locked} />
                                    </Form.Item>
                                </EdocFieldsGrid>
                                <Form.Item name={['destination', 'contactEmail']} label="Destination Contact Email">
                                    <Input placeholder="Email" disabled={locked} />
                                </Form.Item>
                                <Form.Item name={['destination', 'contactPhone']} label="Destination Contact Phone">
                                    <Input placeholder="Phone" disabled={locked} />
                                </Form.Item>
                            </div>
                        );
                    }}
                </Form.Item>
            </div>
        </Form>
    );
};

const TermsStep = ({
    isDark,
    agreeTerms,
    setAgreeTerms,
    signatureName,
    setSignatureName,
    ipAddress,
    agreedAtText,
    termsMarkdown
}) => (
    <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Checkbox checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)}>
                I Agree
            </Checkbox>
        </div>

        <div
            style={{
                padding: 12,
                borderRadius: 12,
                background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)'}`,
                maxHeight: 260,
                overflow: 'auto'
            }}
        >
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
            >
                {termsMarkdown || 'No terms provided.'}
            </ReactMarkdown>
        </div>

        <Form layout="vertical" requiredMark={false}>
            <Form.Item
                label="Electronic signature (Your full name)*"
                validateStatus={agreeTerms && !String(signatureName).trim() ? 'error' : ''}
                help={agreeTerms && !String(signatureName).trim() ? 'Signature is required to book your order.' : null}
            >
                <Input
                    placeholder="Your full name"
                    value={signatureName}
                    onChange={(e) => setSignatureName(e.target.value)}
                    disabled={!agreeTerms}
                />
            </Form.Item>
            <Form.Item label="Your IP address">
                <Input value={ipAddress || '—'} disabled />
            </Form.Item>
            <Form.Item label="Agreed to terms on this day">
                <Input value={agreedAtText} disabled />
            </Form.Item>
        </Form>
    </div>
);

